import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import notificationService from '../services/notificationService';
import { getLastNotificationAction, addNotificationActionListener } from '../services/notificationActionBridge';
import { registerPushToken } from '../redux/actions/notifications';
import { UNIFORM_RESOURCE_LOCATORS, NOTIFICATION_TYPES, AUTOMATED_TASK_NAMES } from '../utils/constants';
import { isLandlordLoggedIn } from '../utils/AuthUtils';
import { Mixpanel } from '../redux/Mixpanel';

const NotificationHandler = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(state => state.authorized);
  const [pendingAction, setPendingAction] = useState(null);

  const trackNotificationEvent = (name, properties = {}) => {
    if (Mixpanel && Mixpanel.track) {
      Mixpanel.track(name, properties);
    }
  };

  const isDismissAction = (actionId) => {
    const id = String(actionId || '').toLowerCase();
    return ['dismiss', 'dismissed', 'close', 'clear', 'cancel'].includes(id);
  };

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web' || platform === 'ios') {
      return;
    }

    notificationService.setupListeners({
      onRegistration: (token) => {
        trackNotificationEvent('notifications:registration_success', { platform });
        dispatch(registerPushToken(token, platform));
      },
      onRegistrationError: (error) => {
        trackNotificationEvent('notifications:registration_error', {
          platform,
          message: error?.message || 'unknown'
        });
        console.error('Error on registration:', error);
      },
      onNotificationReceived: (notification) => {
        const data = notification.data || {};
        trackNotificationEvent('notification: received', {
          notification_type: data.type || 'unknown',
          taskName: data.taskName || undefined,
          notification_id: notification?.id ?? 'unknown',
          source: 'received'
        });
        handleNavigationByType(data, 'view', {
          source: 'received',
          notificationId: notification?.id
        });
      },
      onNotificationAction: async (action) => {
        const notification = action.notification;
        const data = notification.data || {};
        const actionId = action.actionId || data.actionId;

        if (isDismissAction(actionId)) {
          trackNotificationEvent('notification: dismissed', {
            notification_type: data.type || 'unknown',
            actionId: actionId || 'unknown',
            notification_id: notification?.id ?? 'unknown',
            source: 'action_dismiss'
          });
          return;
        }

        trackNotificationEvent('notification: action', {
          notification_type: data.type || 'unknown',
          taskName: data.taskName || undefined,
          actionId: actionId || 'unknown',
          notification_id: notification?.id ?? 'unknown',
          source: 'action'
        });

        await notificationService.storeLastAction({
          data: {
            type: data.type || action.type,
            tenantIds: data.tenantIds || action.tenantIds,
            tenantCount: data.tenantCount || action.tenantCount,
            totalAmount: data.totalAmount || action.totalAmount,
            taskName: data.taskName || action.taskName
          },
          actionId: actionId
        });
        if (!isLoggedIn) {
          setPendingAction({ data, actionId });
          return;
        }
        await handleNavigationByType(data, actionId, {
          source: 'action',
          notificationId: notification?.id
        });
        await notificationService.clearDeliveredNotifications(notification);
        await notificationService.clearStoredLastAction();
      },
      onAppStateChange: async () => {
        await handleNativeNotificationAction();
        await notificationService.checkPendingNotifications();
      },
    });

    const listener = addNotificationActionListener(async (action) => {
      await handleNativeNotificationAction(action);
    });

    if (!isLoggedIn || !isLandlordLoggedIn()) {
      notificationService.reset(true);
    }

    handleNativeNotificationAction();

    return () => {
      listener?.remove();
    };
  }, [isLoggedIn, history, dispatch]);

  useEffect(() => {
    if (isLoggedIn && pendingAction) {
      (async () => {
        await handleNavigationByType(pendingAction.data, pendingAction.actionId, { source: 'pending_action' });
        setPendingAction(null);
        await notificationService.clearDeliveredNotifications();
        await notificationService.clearStoredLastAction();
      })();
    }
  }, [isLoggedIn, pendingAction]);

  const resolveActionPayload = (actionPayload) => {
    if (!actionPayload) return null;
    if (actionPayload.data) {
      return actionPayload;
    }
    return {
      data: {
        type: actionPayload.type,
        tenantIds: actionPayload.tenantIds,
        tenantCount: actionPayload.tenantCount,
        totalAmount: actionPayload.totalAmount,
        taskName: actionPayload.taskName
      },
      actionId: actionPayload.actionId
    };
  };

  const handleNativeNotificationAction = async (incomingAction) => {
    const nativeAction = incomingAction || await getLastNotificationAction();
    const storedAction = await notificationService.getStoredLastAction();
    const action = resolveActionPayload(nativeAction) || resolveActionPayload(storedAction);
    if (!action || (!action.actionId && !action.data?.type)) {
      return;
    }
    const data = action.data;
    if (!isLoggedIn) {
      setPendingAction({ data, actionId: action.actionId });
      return;
    }
    if (isDismissAction(action.actionId)) {
      trackNotificationEvent('notification: dismissed', {
        notification_type: data.type || 'unknown',
        actionId: action.actionId || 'unknown',
        source: incomingAction ? 'native_action' : 'stored_action'
      });
      await notificationService.clearStoredLastAction();
      return;
    }
    trackNotificationEvent('notification: action', {
      notification_type: data.type || 'unknown',
      taskName: data.taskName || undefined,
      actionId: action.actionId || 'unknown',
      source: incomingAction ? 'native_action' : 'stored_action'
    });
    await handleNavigationByType(data, action.actionId, {
      source: incomingAction ? 'native_action' : 'stored_action'
    });
    await notificationService.clearDeliveredNotifications();
    await notificationService.clearStoredLastAction();
  };

  const handleNavigationByType = async (data, actionId, context = {}) => {
    const notificationType = data.type || 'unknown';
    if (data.type === NOTIFICATION_TYPES.payment_reminder) {
      await handlePaymentReminderAction(data, actionId, context);
      return;
    }
    if (data.type === NOTIFICATION_TYPES.payment_summary) {
      trackNotificationEvent('notification: navigation', {
        notification_type: notificationType,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.payments,
        source: context.source || 'unknown'
      });
      history.push(UNIFORM_RESOURCE_LOCATORS.payments, {
        notificationContext: {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          source: context.source || 'unknown'
        }
      });
      return;
    }
    if (data.type === NOTIFICATION_TYPES.invoice_generation_reminder) {
      trackNotificationEvent('notification: navigation', {
        notification_type: notificationType,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.bulkCreateInvoices,
        source: context.source || 'unknown'
      });
      history.push(UNIFORM_RESOURCE_LOCATORS.bulkCreateInvoices, {
        returnToInvoices: true,
        notificationContext: {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          source: context.source || 'unknown'
        }
      });
      return;
    }
    if (data.type === NOTIFICATION_TYPES.utility_bill_generation_reminder) {
      trackNotificationEvent('notification: navigation', {
        notification_type: notificationType,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.utilities,
        source: context.source || 'unknown'
      });
      history.push(UNIFORM_RESOURCE_LOCATORS.utilities, {
        openBulkUtilities: true,
        openBulkUtilitiesAt: Date.now(),
        notificationContext: {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          source: context.source || 'unknown'
        }
      });
      return;
    }
    if (data.type === NOTIFICATION_TYPES.unassigned_payment_summary) {
      trackNotificationEvent('notification: navigation', {
        notification_type: notificationType,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.payments,
        source: context.source || 'unknown',
        unassigned_payments: true
      });
      history.push(UNIFORM_RESOURCE_LOCATORS.payments, {
        unassignedPayments: true,
        notificationContext: {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          source: context.source || 'unknown'
        }
      });
      return;
    }
    if (data.type === NOTIFICATION_TYPES.automated_task_completed) {
      const taskName = data.taskName;
      if (taskName === AUTOMATED_TASK_NAMES.automaticInvoiceGeneration) {
        trackNotificationEvent('notification: navigation', {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          destination: UNIFORM_RESOURCE_LOCATORS.invoices,
          source: context.source || 'unknown'
        });
        history.push(UNIFORM_RESOURCE_LOCATORS.invoices, {
          notificationContext: {
            notification_type: notificationType,
            actionId: actionId || 'unknown',
            source: context.source || 'unknown',
            taskName
          }
        });
        return;
      }
      if (taskName === AUTOMATED_TASK_NAMES.automaticTenantBalanceReminder) {
        const notificationFilters = { minBalance: 0.01 };
        trackNotificationEvent('notification: navigation', {
          notification_type: notificationType,
          actionId: actionId || 'unknown',
          destination: UNIFORM_RESOURCE_LOCATORS.tenants,
          source: context.source || 'unknown'
        });
        history.push({
          pathname: UNIFORM_RESOURCE_LOCATORS.tenants,
          state: { notificationFilters }
        });
        return;
      }
    }
    if (isLoggedIn) {
      trackNotificationEvent('notification: navigation', {
        notification_type: notificationType,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.dashboard,
        source: context.source || 'unknown'
      });
      history.push(UNIFORM_RESOURCE_LOCATORS.dashboard);
    }
  };


  const handlePaymentReminderAction = async (data, actionId, context = {}) => {
    Mixpanel.track('notification: payment_reminder action', {
      notification_type: 'payment_reminder',
      actionId: actionId || 'unknown',
      tenantCount: data.tenantCount || '0',
      totalAmount: data.totalAmount || '0',
      source: context.source || 'unknown'
    });

    if (actionId === 'yes') {

      handlePaymentReminderNavigate(data, 'yes', context);
    } else if (actionId === 'no') {
      // Backend actionId "no" corresponds to the "View Tenants" CTA; route to tenants.
      handlePaymentReminderNavigate(data, 'view', context);
    } else if (actionId === 'view' || !actionId) {
      handlePaymentReminderNavigate(data, 'view', context);
    }
  };

  const handlePaymentReminderNavigate = (data, actionId, context = {}) => {
    if (isLoggedIn) {
      if (actionId === 'view') {
          const notificationFilters = { minBalance: 0.01 };
          trackNotificationEvent('notification: navigation', {
            notification_type: NOTIFICATION_TYPES.payment_reminder,
            actionId: actionId || 'unknown',
            destination: UNIFORM_RESOURCE_LOCATORS.tenants,
            source: context.source || 'unknown'
          });
          history.push({
            pathname: UNIFORM_RESOURCE_LOCATORS.tenants,
            state: {
              notificationFilters
            }
          });
        } else if (actionId === 'yes') {
          trackNotificationEvent('notification: navigation', {
            notification_type: NOTIFICATION_TYPES.payment_reminder,
            actionId: actionId || 'unknown',
            destination: UNIFORM_RESOURCE_LOCATORS.dashboard,
            source: context.source || 'unknown',
            open_bulk_payments: true
          });
          history.push(UNIFORM_RESOURCE_LOCATORS.dashboard, {
            openBulkPayments: true,
            notificationContext: {
              notification_type: NOTIFICATION_TYPES.payment_reminder,
              actionId: actionId || 'unknown',
              source: context.source || 'unknown'
            }
          });
        }
    } else {
      const redirectTarget = actionId === 'yes'
        ? UNIFORM_RESOURCE_LOCATORS.dashboard
        : UNIFORM_RESOURCE_LOCATORS.tenants;
      trackNotificationEvent('notification: navigation', {
        notification_type: NOTIFICATION_TYPES.payment_reminder,
        actionId: actionId || 'unknown',
        destination: UNIFORM_RESOURCE_LOCATORS.appLogin,
        source: context.source || 'unknown'
      });
      history.push({
        pathname: UNIFORM_RESOURCE_LOCATORS.appLogin,
        search: `?redirectTo=${redirectTarget}`
      });
    }
  };

  return null;
};

export default NotificationHandler;
